import { type ReactNode } from 'react';
import { cn } from '../lib/utils';
import { Select, SelectContent, SelectTrigger, SelectValue } from './Select';

type SelectValue = string | number | null;

interface SelectProps<T extends SelectValue = string> {
  children?: ReactNode;
  className?: string;
  'data-testid'?: string;
  defaultValue?: T;
  disabled?: boolean;
  id?: string;
  onChange?: (value: T) => void;
  placeholder?: ReactNode;
  value?: T;
  'aria-invalid'?: boolean;
  'aria-label'?: string;
  /** The trigger's own height variant: `sm` is 32px, `default` 36px. */
  size?: 'sm' | 'default';
}

function toSelectString(value?: SelectValue): string | undefined {
  return value === undefined ? undefined : String(value);
}

function fromSelectString<T extends SelectValue>(next: string, isNumber: boolean): T {
  if (isNumber) {
    return Number(next) as T;
  }
  return next as T;
}

// FIXME: Replace me with react-select, because:
//
// - shadcn select doesn't let us forward a ref to the underlying <select> element.
// - shadcn select doesn't support multi-select.
//

/**
 * Renders a Select box.
 *
 * This is a simple wrapper around Radix UI's Select component.
 *
 * @example
 *   <SimpleSelect className="w-[200px]">
 *     <SelectItem value="apple">Apple</SelectItem>
 *     <SelectItem value="banana">Banana</SelectItem>
 *   </SimpleSelect>
 *
 * @param props
 * @returns
 */
export function SimpleSelect<T extends SelectValue = string>({
  children,
  className,
  defaultValue,
  disabled,
  id,
  onChange,
  placeholder,
  value,
  size,
  'data-testid': dataTestId,
  'aria-invalid': ariaInvalid,
  'aria-label': ariaLabel,
}: SelectProps<T>) {
  const isNumber = typeof (value ?? defaultValue) === 'number';

  return (
    <Select
      value={toSelectString(value)}
      defaultValue={toSelectString(defaultValue)}
      onValueChange={(next) => onChange?.(fromSelectString<T>(next, isNumber))}
      disabled={disabled}
    >
      <SelectTrigger
        id={id}
        size={size}
        className={cn(className, 'aria-invalid:ring-1 aria-invalid:ring-destructive')}
        data-testid={dataTestId}
        aria-invalid={ariaInvalid}
        aria-label={ariaLabel}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent
        position="popper"
        align="start"
        // Add scrolling to the Select content to view large lists
        className="max-h-[var(--radix-select-content-available-height)]"
      >
        {children}
      </SelectContent>
    </Select>
  );
}

SimpleSelect.displayName = 'SimpleSelect';
